import {useCallback, useState} from 'react';

import {useImageSelection} from '@dms/shared';

/**
 * Audit photo bulk-selection state. Builds on the shared image-selection set with
 * a registry of the image ids that are actually rendered as selectable (reported
 * by the AuditImage galleries). This keeps "select all", the selected count and
 * the bulk-action availability scoped to images the user can really see and
 * toggle — images shown only as in-progress upload tiles never register, so they
 * are not wrongly selected or deleted.
 */
export function useAuditImageSelection() {
  const selection = useImageSelection();
  const [selectableImageIds, setSelectableImageIds] = useState<Set<string>>(new Set());

  // useCallback is required here: the AuditImage
  // registration effect depends on these identities, so they must stay stable to
  // avoid an unregister/register render loop.
  const registerSelectableImage = useCallback((imageId: string) => {
    setSelectableImageIds((previous) =>
      previous.has(imageId) ? previous : new Set(previous).add(imageId)
    );
  }, []);

  const unregisterSelectableImage = useCallback((imageId: string) => {
    setSelectableImageIds((previous) => {
      if (!previous.has(imageId)) {
        return previous;
      }
      const updated = new Set(previous);
      updated.delete(imageId);
      return updated;
    });
  }, []);

  const selectAllImages = () => {
    selection.selectImagesInBulk(Array.from(selectableImageIds));
  };

  return {
    ...selection,
    hasSelectableImages: selectableImageIds.size > 0,
    registerSelectableImage,
    unregisterSelectableImage,
    selectAllImages,
  };
}
